import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Nav, Alert, Modal, Form } from "react-bootstrap";
import { Page } from "containers/Page";
import { PrimaryButton } from "components/Button/style";
import { DropdownSelect } from "components/DropdownSelect";
import { toastErro } from "components/Toast";
import { alertaWarnig } from "components/Alerta";
import { useLoteEstampagem } from "./hooks/useLoteEstampagem";
import { Filtros, STATUS_EXCETO_FINALIZADO } from "./containers/Filtros";
import { Listar } from "./containers/Listar";
import { CriarLoteModal } from "./modals/CriarLoteModal";
import { FiltrosLote, TipoLote } from "./interfaces/ILoteEstampagem";

export function LoteEstampagem() {
  const navigate = useNavigate();
  const {
    lotes,
    loading,
    impressora,
    erroImpressora,
    verificarImpressora,
    buscarLotes,
    criarLote,
    finalizarLote,
    avancarStatus,
    vincularAr,
    abrirProcesso,
    exportarLoteDados,
    buscarResumo,
    imprimirEtiquetaPdf,
    impressoras,
    setImpressora,
    carregarImpressoras,
    verificarEntregaCorreios,
    buscarEventosCorreios,
  } = useLoteEstampagem();

  const [showCriar, setShowCriar] = useState(false);
  const [tipoNovo, setTipoNovo] = useState<TipoLote>('ESTAMPAGEM');
  const [loadingCriar, setLoadingCriar] = useState(false);
  const [loadingFinalizarId, setLoadingFinalizarId] = useState<number | null>(null);
  const [loadingGuiaId, setLoadingGuiaId] = useState<number | null>(null);
  const [loadingAvancarId, setLoadingAvancarId] = useState<number | null>(null);
  const [loadingArId, setLoadingArId] = useState<number | null>(null);
  const [loadingVerificarTodos, setLoadingVerificarTodos] = useState(false);
  const [resultadoCorreios, setResultadoCorreios] = useState<Array<{ id: number; nomeEmpresa: string; ar: string; entregue: boolean; erro?: string }> | null>(null);
  const [loadingRastreioId, setLoadingRastreioId] = useState<number | null>(null);
  const [rastreioModal, setRastreioModal] = useState<{ loteId: number; ar: string; eventos: string[][]; erro: string | null } | null>(null);
  const [loadingExportarSelecionados, setLoadingExportarSelecionados] = useState(false);
  const [modalArLoteId, setModalArLoteId] = useState<number | null>(null);
  const [arInput, setArInput] = useState("");
  const arInputRef = useRef<HTMLInputElement>(null);
  const [errosEtapa, setErrosEtapa] = useState<Array<{ placa: string; idProcesso: string; mensagemErro?: string }> | null>(null);
  const [errosEtapaLoteId, setErrosEtapaLoteId] = useState<number | null>(null);

  const [tabAtiva, setTabAtiva] = useState<TipoLote>('ESTAMPAGEM');
  const [lotesSelecionados, setLotesSelecionados] = useState<number[]>([]);
  const [filtroStatusDisplay, setFiltroStatusDisplay] = useState<string>(STATUS_EXCETO_FINALIZADO);

  const handleFiltrar = useCallback((filtros: FiltrosLote & { statusDisplay?: string }) => {
    setLotesSelecionados([]);
    setFiltroStatusDisplay(filtros.statusDisplay ?? filtros.status);
    const statusParam = filtros.statusDisplay === STATUS_EXCETO_FINALIZADO ? '' : filtros.status;
    buscarLotes({ ...filtros, status: statusParam as any, tipo: tabAtiva === 'ESTAMPAGEM' ? undefined : tabAtiva });
  }, [buscarLotes, tabAtiva]);

  const handleTab = useCallback((tab: TipoLote) => {
    setTabAtiva(tab);
    setLotesSelecionados([]);
    buscarLotes({ status: '', tipo: tab === 'ESTAMPAGEM' ? undefined : tab });
  }, [buscarLotes]);

  const handleToggleSelecionado = useCallback((id: number) => {
    setLotesSelecionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleToggleTodos = useCallback(() => {
    const todosIds = lotes.map((l) => l.id);
    const todosSelecionados = todosIds.every((id) => lotesSelecionados.includes(id));
    setLotesSelecionados(todosSelecionados ? [] : todosIds);
  }, [lotes, lotesSelecionados]);

  const handleExportarSelecionados = useCallback(async () => {
    if (lotesSelecionados.length === 0) { return; }
    setLoadingExportarSelecionados(true);
    try {
      const todasLinhas: any[] = [];
      for (const id of lotesSelecionados) {
        const linhas = await exportarLoteDados(id);
        todasLinhas.push(...linhas);
      }
      const ws = XLSX.utils.json_to_sheet(todasLinhas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Estampagem');
      XLSX.writeFile(wb, "lotes-estampagem-" + lotesSelecionados.join("-") + ".xlsx");
    } catch (error: any) {
      toastErro(error?.message ?? "Erro ao exportar lotes");
    } finally {
      setLoadingExportarSelecionados(false);
    }
  }, [lotesSelecionados, exportarLoteDados]);

  const handleContinuar = useCallback(async (dados: { idEmpresa: number; nomeEmpresa: string; localEntrega?: string; comTag: boolean; tagJaRegistrada: boolean; tipo: TipoLote }) => {
    setLoadingCriar(true);
    try {
      const lote = await criarLote({ ...dados, tipo: dados.tipo });
      setShowCriar(false);
      navigate(`/backoffice/estampagem-placa/${lote.id}`, {
        state: dados.tipo !== 'TAGS' ? { abrirLeitura: true } : undefined,
      });
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao criar lote de estampagem");
    } finally {
      setLoadingCriar(false);
    }
  }, [criarLote, navigate]);

  const handleImprimirGuia = useCallback(async (id: number) => {
    setLoadingGuiaId(id);
    try {
      const { lote } = await buscarResumo(id);
      const linhas: any[] = await exportarLoteDados(id);
      const linhasHtml = linhas.map((row: any) =>
        "<tr><td>" + (row["Placa"] ?? "-") + "</td><td>" + (row["Renavam"] ?? "-") + "</td><td>" + (row["Chassi"] ?? "-") + "</td><td>" + (row["TAG"] ?? "-") + "</td></tr>"
      ).join("");
      const html = [
        "<!DOCTYPE html><html><head><meta charset='UTF-8'>",
        "<title>Protocolo de Entrega #" + lote.id + "</title>",
        "<style>",
        "* { margin: 0; padding: 0; box-sizing: border-box; }",
        "body { font-family: Arial, sans-serif; font-size: 11pt; color: #111; }",
        ".header { position: relative; height: 80px; margin-bottom: 30px; }",
        ".header-badge { position: absolute; top: 0; right: 0; width: 260px; height: 80px; background: #1b3c6e; clip-path: polygon(22% 0, 100% 0, 100% 100%, 0 100%); display: flex; align-items: center; justify-content: flex-end; padding-right: 22px; gap: 10px; color: white; }",
        ".lw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; width: 24px; height: 24px; }",
        ".lw-grid span { background: white; border-radius: 1px; display: block; }",
        ".lw-name { font-size: 15pt; font-weight: 400; letter-spacing: 1px; }",
        ".lw-name b { font-weight: 900; }",
        "h1 { text-align: center; font-size: 15pt; font-weight: 900; letter-spacing: 2px; margin-bottom: 28px; padding: 0 40px; }",
        ".info { padding: 0 40px; margin-bottom: 20px; line-height: 2; }",
        ".info p { font-size: 11pt; }",
        "body { padding-bottom: 220px; }",
        "table { border-collapse: collapse; margin: 0 40px 20px; width: calc(100% - 80px); }",
        "th, td { border: 1px solid #aaa; padding: 3px 10px; font-size: 10pt; }",
        ".bottom-fixed { position: fixed; bottom: 0; left: 0; right: 0; background: white; }",
        ".sign { padding: 14px 40px 8px; }",
        ".sign p { margin-bottom: 14px; font-size: 10pt; }",
        ".footer-wave { line-height: 0; }",
        ".footer-wave svg { display: block; width: 100%; }",
        ".footer-body { background: #1b3c6e; color: white; padding: 12px 30px; display: flex; justify-content: space-between; align-items: center; }",
        ".f-left { display: flex; align-items: center; gap: 12px; }",
        ".f-qr { width: 56px; height: 56px; border: 1.5px solid rgba(255,255,255,0.5); display: flex; align-items: center; justify-content: center; font-size: 7pt; text-align: center; color: rgba(255,255,255,0.7); }",
        ".f-brand-name { font-size: 11pt; }",
        ".f-brand-name b { font-weight: 900; }",
        ".f-social { display: flex; gap: 5px; margin: 3px 0; }",
        ".f-social span { width: 18px; height: 18px; border: 1px solid white; border-radius: 3px; display: flex; align-items: center; justify-content: center; font-size: 8pt; font-weight: bold; }",
        ".f-linktree { font-size: 7pt; opacity: 0.75; }",
        ".f-right { text-align: right; }",
        ".f-phone { font-size: 13pt; font-weight: bold; margin-bottom: 4px; }",
        ".f-addr { font-size: 9pt; opacity: 0.9; }",
        "@media print { @page { margin: 12mm 15mm 0 15mm; } @page :first { margin: 0; } }",
        "</style></head><body>",
        "<div class='header'>",
        "  <div class='header-badge'>",
        "    <div class='lw-grid'><span></span><span></span><span></span><span></span></div>",
        "    <div class='lw-name'>LW<b>Tecnologia</b></div>",
        "  </div>",
        "</div>",
        "<h1>PROTOCOLO DE ENTREGA DE PLACAS</h1>",
        "<div class='info'>",
        "  <p><b>CLIENTE:</b> " + lote.nomeEmpresa + "</p>",
        "  <p><b>QUANTIDADE:</b> " + linhas.length + " placa(s)</p>",
        "  <p><b>ENTREGA:</b> " + (lote.localEntrega ?? "\u2014") + "</p>",
        "  <p><b>LOTE:</b> #" + lote.id + "</p>",
        "</div>",
        "<table>",
        "  <thead><tr><th>Placa</th><th>Renavam</th><th>Chassi</th><th>Tag</th></tr></thead>",
        "  <tbody>" + linhasHtml + "</tbody>",
        "</table>",
        "<div class='bottom-fixed'>",
        "  <div class='sign'>",
        "    <p>Curitiba ___/____/___________.</p>",
        "    <p>Hor\u00e1rio:_______:_______.</p>",
        "    <p>Assinatura (Nome leg\u00edvel):_______________________________________________</p>",
        "  </div>",
        "  <div class='footer-wave'>",
        "    <svg viewBox='0 0 1440 50' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none' height='40'>",
        "      <path d='M0,50 C400,0 1040,0 1440,50 L1440,50 L0,50 Z' fill='#1b3c6e'/>",
        "    </svg>",
        "  </div>",
        "  <div class='footer-body'>",
        "    <div class='f-left'>",
        "      <div>",
        "        <div class='f-brand-name'>LW<b>Tecnologia</b></div>",
        "      </div>",
        "    </div>",
        "    <div class='f-right'>",
        "      <div class='f-phone'>(41) 3501-8315</div>",
        "      <div class='f-addr'>Rua XV de Novembro, 621 3\u00ba Andar<br>Centro, Curitiba/PR</div>",
        "    </div>",
        "  </div>",
        "</div>",
        "<script>window.print();window.close();<\/script>",
        "</body></html>",
      ].join("\n");
      const win = window.open("", "_blank");
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao gerar guia");
    } finally {
      setLoadingGuiaId(null);
    }
  }, [buscarResumo, exportarLoteDados]);

  const handleFinalizar = useCallback(async (id: number) => {
    const { isConfirmed } = await alertaWarnig(
      "Finalizar estampagem",
      "Deseja finalizar esta estampagem?",
      { showCancelButton: true, confirmButtonText: "Finalizar" }
    );
    if (!isConfirmed) { return; }
    setLoadingFinalizarId(id);
    try {
      await finalizarLote(id);
      await handleImprimirGuia(id);
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao finalizar lote de estampagem");
    } finally {
      setLoadingFinalizarId(null);
    }
  }, [finalizarLote, handleImprimirGuia]);

  const extrairErrosEtapa = (error: any): Array<{ placa: string; idProcesso: string }> | null => {
    const data = error?.response?.data;
    if (data?.erros?.length) { return data.erros; }
    return null;
  };

  const handleAvancarStatus = useCallback(async (id: number) => {
    setLoadingAvancarId(id);
    try {
      await avancarStatus(id);
    } catch (error: any) {
      const erros = extrairErrosEtapa(error);
      if (erros) { setErrosEtapa(erros); setErrosEtapaLoteId(id); }
      else { toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao avançar status"); }
    } finally {
      setLoadingAvancarId(null);
    }
  }, [avancarStatus]);

  const handleAlterarStatusManual = useCallback(async () => {
    if (!errosEtapaLoteId) { return; }
    setLoadingAvancarId(errosEtapaLoteId);
    try {
      await avancarStatus(errosEtapaLoteId, true);
      setErrosEtapa(null);
      setErrosEtapaLoteId(null);
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao alterar status");
    } finally {
      setLoadingAvancarId(null);
    }
  }, [errosEtapaLoteId, avancarStatus]);

  const handleVerificarTodosEnviados = useCallback(async () => {
    const enviados = lotes.filter((l) => l.status === "ENVIADO" && l.ar);
    if (enviados.length === 0) { toastErro("Nenhum lote enviado com AR para verificar."); return; }
    setLoadingVerificarTodos(true);
    const resultados: Array<{ id: number; nomeEmpresa: string; ar: string; entregue: boolean; erro?: string }> = [];
    for (const lote of enviados) {
      try {
        const entregue = await verificarEntregaCorreios(lote.id, lote.ar!);
        resultados.push({ id: lote.id, nomeEmpresa: lote.nomeEmpresa, ar: lote.ar!, entregue });
      } catch (error: any) {
        resultados.push({ id: lote.id, nomeEmpresa: lote.nomeEmpresa, ar: lote.ar!, entregue: false, erro: error?.response?.data?.message ?? error?.message ?? "Erro" });
      }
    }
    setLoadingVerificarTodos(false);
    setResultadoCorreios(resultados);
  }, [lotes, verificarEntregaCorreios]);

  const handleAbrirRastreio = useCallback(async (id: number) => {
    const lote = lotes.find((l) => l.id === id);
    if (!lote?.ar) { return; }
    setLoadingRastreioId(id);
    try {
      const eventos = await buscarEventosCorreios(lote.ar);
      setRastreioModal({ loteId: id, ar: lote.ar, eventos, erro: null });
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.message ?? "Erro ao consultar Correios";
      setRastreioModal({ loteId: id, ar: lote.ar, eventos: [], erro: msg });
    } finally {
      setLoadingRastreioId(null);
    }
  }, [lotes, buscarEventosCorreios]);

  const handleAvancarParaEntregue = useCallback(async () => {
    if (!rastreioModal) { return; }
    setLoadingAvancarId(rastreioModal.loteId);
    try {
      await avancarStatus(rastreioModal.loteId);
      setRastreioModal(null);
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao avançar status");
    } finally {
      setLoadingAvancarId(null);
    }
  }, [rastreioModal, avancarStatus]);

  const handleAbrirModalAr = useCallback((id: number) => {
    const lote = lotes.find((l) => l.id === id);
    setArInput(lote?.ar ?? "");
    setModalArLoteId(id);
    setTimeout(() => arInputRef.current?.focus(), 100);
  }, [lotes]);

  const handleConfirmarAr = useCallback(async () => {
    if (!modalArLoteId || !arInput.trim()) { return; }
    const idLote = modalArLoteId;
    setLoadingArId(idLote);
    try {
      await vincularAr(idLote, arInput.trim());
      setModalArLoteId(null);
      setArInput("");
      try {
        await avancarStatus(idLote);
      } catch (error: any) {
        const erros = extrairErrosEtapa(error);
        if (erros) { setErrosEtapa(erros); setErrosEtapaLoteId(idLote); }
        else { toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao avançar status para Enviado"); }
      }
    } catch (error: any) {
      const erros = extrairErrosEtapa(error);
      if (erros) { setModalArLoteId(null); setArInput(""); setErrosEtapa(erros); setErrosEtapaLoteId(idLote); }
      else { toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao vincular AR"); }
    } finally {
      setLoadingArId(null);
    }
  }, [modalArLoteId, arInput, vincularAr, avancarStatus]);

  const testeEtiqueta = {
    empresa: "Empresa Teste",
    placa: "TEST001",
    data: new Date().toLocaleDateString("pt-BR"),
    marcaModelo: "MARCA/MODELO TESTE",
    cor: "BRANCO",
    localLacre: "LOCAL TESTE",
    chassi: "9BGEA48A0SG000000",
  };

  return (
    <Page lastPage="Home / Backoffice" title="Estampagem de Placa">
      {impressora ? (
        <Alert variant="success" className="mt-3 mx-3 py-2">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span><strong>Impressora:</strong> {impressora.name}</span>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Teste:</small>
              {impressoras.length === 0 ? (
                <PrimaryButton outline variante="primary" buttonWidth="lg" buttonWidthTipo="px" onClick={carregarImpressoras}>
                  Ver impressoras
                </PrimaryButton>
              ) : (
                <div style={{ minWidth: 220 }}>
                  <DropdownSelect
                    options={impressoras.map((imp) => ({ value: imp.uid, label: imp.name }))}
                    title=""
                    selectedValue={impressora.uid}
                    onSelect={(uid) => {
                      const selecionada = impressoras.find((imp) => imp.uid === uid);
                      if (selecionada) { setImpressora(selecionada); }
                    }}
                  />
                </div>
              )}
              <PrimaryButton outline variante="primary" buttonWidth="md" buttonWidthTipo="px" onClick={() => imprimirEtiquetaPdf(testeEtiqueta)}>
                PDF
              </PrimaryButton>
            </div>
          </div>
        </Alert>
      ) : (
        <Alert variant={erroImpressora ? "warning" : "secondary"} className="mt-3 mx-3 py-2">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>
              {erroImpressora
                ? <><strong>Impressora não conectada:</strong> {erroImpressora}</>
                : <span>Clique em <strong>Verificar impressora</strong> para conectar.</span>
              }
            </span>
            <PrimaryButton outline variante="primary" onClick={verificarImpressora}>
              {erroImpressora ? "Tentar novamente" : "Verificar impressora"}
            </PrimaryButton>
          </div>
        </Alert>
      )}

      <div className="px-3 pt-3">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link active={tabAtiva === 'ESTAMPAGEM'} onClick={() => handleTab('ESTAMPAGEM')}>
              Estampagem
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link active={tabAtiva === 'TAGS'} onClick={() => handleTab('TAGS')}>
              Tags
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      <Filtros
        onFiltrar={handleFiltrar}
        onNovoBotao={() => { setTipoNovo(tabAtiva); setShowCriar(true); }}
        totalSelecionados={lotesSelecionados.length}
        loadingExportar={loadingExportarSelecionados}
        onExportarSelecionados={handleExportarSelecionados}
        loadingVerificarTodos={loadingVerificarTodos}
        onVerificarTodosEnviados={handleVerificarTodosEnviados}
      />

      <Listar
        lotes={(filtroStatusDisplay === STATUS_EXCETO_FINALIZADO ? lotes.filter((l) => l.status !== "FINALIZADO") : lotes)
          .filter((l) => tabAtiva === 'TAGS' ? l.tipo === 'TAGS' : l.tipo !== 'TAGS')}
        loading={loading}
        loadingFinalizarId={loadingFinalizarId}
        loadingGuiaId={loadingGuiaId}
        loadingAvancarId={loadingAvancarId}
        loadingArId={loadingArId}
        selecionados={lotesSelecionados}
        onToggleSelecionado={handleToggleSelecionado}
        onToggleTodos={handleToggleTodos}
        loadingRastreioId={loadingRastreioId}
        onFinalizar={handleFinalizar}
        onImprimirGuia={handleImprimirGuia}
        onAvancarStatus={handleAvancarStatus}
        onVincularAr={handleAbrirModalAr}
        onAbrirRastreio={handleAbrirRastreio}
      />

      <Modal show={modalArLoteId !== null} onHide={() => { setModalArLoteId(null); setArInput(""); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Vincular AR — Lote #{modalArLoteId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Código AR (rastreio)</Form.Label>
            <Form.Control
              ref={arInputRef}
              type="text"
              placeholder="Ex: BR123456789BR"
              value={arInput}
              onChange={(e) => setArInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") { handleConfirmarAr(); } }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton outline variante="primary" onClick={() => { setModalArLoteId(null); setArInput(""); }}>
            Cancelar
          </PrimaryButton>
          <PrimaryButton
            variante="primary"
            disabled={!arInput.trim() || loadingArId !== null}
            onClick={handleConfirmarAr}
          >
            {loadingArId !== null ? "Salvando..." : "Confirmar"}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      <Modal show={errosEtapa !== null} onHide={() => { setErrosEtapa(null); setErrosEtapaLoteId(null); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", color: "#dc3545" }}>Erro ao disparar etapas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Os seguintes processos não puderam ter a etapa disparada. O status <strong>não foi alterado</strong>.</p>
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr><th>Placa</th><th>ID Processo</th><th>Mensagem</th></tr>
            </thead>
            <tbody>
              {(errosEtapa ?? []).map((e) => (
                <tr key={e.idProcesso}>
                  <td>{e.placa}</td>
                  <td>
                    <button
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#0d6efd", padding: 0, textDecoration: "underline" }}
                      onClick={() => abrirProcesso(e.idProcesso)}
                    >
                      {e.idProcesso}
                    </button>
                  </td>
                  <td style={{ fontSize: "0.8rem", color: "#6c757d" }}>{e.mensagemErro ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton outline variante="primary" onClick={() => { setErrosEtapa(null); setErrosEtapaLoteId(null); }}>
            Fechar
          </PrimaryButton>
          {errosEtapaLoteId !== null && (
            <PrimaryButton
              variante="primary"
              disabled={loadingAvancarId !== null}
              onClick={handleAlterarStatusManual}
            >
              {loadingAvancarId !== null ? "Alterando..." : "Alterar Status manual"}
            </PrimaryButton>
          )}
        </Modal.Footer>
      </Modal>

      {(() => {
        const loteRastreio = rastreioModal ? lotes.find((l) => l.id === rastreioModal.loteId) : null;
        const isEntregue = rastreioModal?.eventos.some((row) =>
          row.some((c) => typeof c === 'string' && c.toUpperCase().includes('ENTREGUE'))
        ) ?? false;
        return (
          <Modal show={rastreioModal !== null} onHide={() => setRastreioModal(null)} centered size="lg">
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: "1rem" }}>
                Rastreio Correios — {rastreioModal?.ar}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <a
                  href={`https://rastreamento.correios.com.br/app/index.php?objetos=${rastreioModal?.ar ?? ""}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: "0.82rem" }}
                >
                  Abrir no site dos Correios ↗
                </a>
              </div>
              {rastreioModal?.erro ? (
                <div className="alert alert-warning py-2 mb-0" style={{ fontSize: "0.85rem" }}>
                  <strong>Não foi possível carregar os eventos:</strong> {rastreioModal.erro}
                </div>
              ) : rastreioModal?.eventos.length === 0 ? (
                <p className="text-muted mb-0">Nenhuma informação encontrada para este código.</p>
              ) : (
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr><th>Data / Hora</th><th>Evento</th><th>Local</th></tr>
                  </thead>
                  <tbody>
                    {(rastreioModal?.eventos ?? []).map((row, i) => {
                      const isEntregueRow = row.some((c) => typeof c === 'string' && c.toUpperCase().includes('ENTREGUE'));
                      return (
                        <tr key={i} style={isEntregueRow ? { background: "#d1e7dd" } : undefined}>
                          <td style={{ whiteSpace: "nowrap", fontSize: "0.82rem" }}>{row[0] ?? "—"}</td>
                          <td style={{ fontSize: "0.82rem", fontWeight: isEntregueRow ? 700 : undefined, color: isEntregueRow ? "#198754" : undefined }}>{row[1] ?? "—"}</td>
                          <td style={{ fontSize: "0.82rem", color: "#6c757d" }}>{row[2] ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Modal.Body>
            <Modal.Footer>
              <PrimaryButton outline variante="primary" onClick={() => setRastreioModal(null)}>Fechar</PrimaryButton>
              {loteRastreio?.status === "ENVIADO" && isEntregue && (
                <PrimaryButton
                  variante="primary"
                  disabled={loadingAvancarId !== null}
                  onClick={handleAvancarParaEntregue}
                >
                  {loadingAvancarId !== null ? "Avançando..." : "Avançar para Entregue"}
                </PrimaryButton>
              )}
            </Modal.Footer>
          </Modal>
        );
      })()}

      <Modal show={resultadoCorreios !== null} onHide={() => setResultadoCorreios(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Resultado — Verificação de Entregas nos Correios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr><th>Lote</th><th>Empresa</th><th>AR</th><th>Resultado</th></tr>
            </thead>
            <tbody>
              {(resultadoCorreios ?? []).map((r) => (
                <tr key={r.id}>
                  <td>#{r.id}</td>
                  <td>{r.nomeEmpresa}</td>
                  <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{r.ar}</td>
                  <td>
                    {r.erro ? (
                      <span style={{ color: "#dc3545", fontSize: "0.82rem" }}>Erro: {r.erro}</span>
                    ) : r.entregue ? (
                      <span style={{ color: "#198754", fontWeight: 600 }}>✓ Entregue — status atualizado</span>
                    ) : (
                      <span style={{ color: "#fd7e14" }}>Não entregue ainda</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton variante="primary" onClick={() => setResultadoCorreios(null)}>Fechar</PrimaryButton>
        </Modal.Footer>
      </Modal>

      <CriarLoteModal
        show={showCriar}
        loading={loadingCriar}
        tipoInicial={tipoNovo}
        onFechar={() => setShowCriar(false)}
        onContinuar={handleContinuar}
      />
    </Page>
  );
}
